import { useRef, useState, useCallback, useEffect } from 'react';

function rectsOverlap(a, b) {
  return !(
    a.left + a.width < b.left ||
    b.left + b.width < a.left ||
    a.top + a.height < b.top ||
    b.top + b.height < a.top
  );
}

function hitTestTiles(container, clientRect) {
  if (!container) return [];
  const tiles = container.querySelectorAll('[data-image-id]');
  const hits = [];
  for (const tile of tiles) {
    const r = tile.getBoundingClientRect();
    if (rectsOverlap(clientRect, { left: r.left, top: r.top, width: r.width, height: r.height })) {
      hits.push(tile.getAttribute('data-image-id'));
    }
  }
  return hits;
}

function makeClientRect(startClient, endClient) {
  const left = Math.min(startClient.x, endClient.x);
  const top = Math.min(startClient.y, endClient.y);
  return {
    left,
    top,
    width: Math.abs(endClient.x - startClient.x),
    height: Math.abs(endClient.y - startClient.y),
  };
}

const SCROLL_EDGE = 60;
const SCROLL_SPEED = 8;

/**
 * Drag-to-select hook. Activated from a triangle chip on each tile.
 * Draws a marquee rectangle from the start point and selects all tiles
 * whose bounds overlap with it — desktop OS-style selection.
 */
export function useDragSelect({ containerRef, onSelectionChange, enabled = true }) {
  const lassoRef = useRef(null);
  // Start point in page coords (tracks document position during scroll)
  const startPage = useRef(null);
  const isDragging = useRef(false);
  const baseSelection = useRef([]);
  const scrollRAF = useRef(null);
  const currentMouseY = useRef(0);
  const currentClientPos = useRef(null);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const stopAutoScroll = useCallback(() => {
    if (scrollRAF.current) {
      cancelAnimationFrame(scrollRAF.current);
      scrollRAF.current = null;
    }
  }, []);

  const getStartClient = useCallback(() => {
    if (!startPage.current) return { x: 0, y: 0 };
    return {
      x: startPage.current.px - window.scrollX,
      y: startPage.current.py - window.scrollY,
    };
  }, []);

  const updateRectAndHitTest = useCallback(() => {
    if (!currentClientPos.current || !startPage.current) return;
    const startClient = getStartClient();
    const rect = makeClientRect(startClient, currentClientPos.current);

    // Update visual overlay
    if (lassoRef.current) {
      Object.assign(lassoRef.current.style, {
        display: 'block',
        left: rect.left + 'px',
        top: rect.top + 'px',
        width: rect.width + 'px',
        height: rect.height + 'px',
      });
    }

    // Hit test all tiles against the rectangle
    const hits = hitTestTiles(containerRef.current, rect);
    const merged = [...new Set([...baseSelection.current, ...hits])];
    onSelectionChangeRef.current(merged);
  }, [containerRef, getStartClient]);

  const autoScroll = useCallback((clientY) => {
    currentMouseY.current = clientY;
    stopAutoScroll();

    const step = () => {
      const y = currentMouseY.current;
      const vh = window.innerHeight;
      let delta = 0;
      if (y < SCROLL_EDGE) {
        delta = -SCROLL_SPEED * (1 - y / SCROLL_EDGE);
      } else if (y > vh - SCROLL_EDGE) {
        delta = SCROLL_SPEED * (1 - (vh - y) / SCROLL_EDGE);
      }
      if (delta !== 0) {
        window.scrollBy(0, delta);
        updateRectAndHitTest();
        scrollRAF.current = requestAnimationFrame(step);
      }
    };
    scrollRAF.current = requestAnimationFrame(step);
  }, [stopAutoScroll, updateRectAndHitTest]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    currentClientPos.current = { x: e.clientX, y: e.clientY };
    updateRectAndHitTest();
    autoScroll(e.clientY);
  }, [updateRectAndHitTest, autoScroll]);

  const handleMouseUp = useCallback(() => {
    window.removeEventListener('mousemove', stableMove.current);
    window.removeEventListener('mouseup', stableUp.current);
    stopAutoScroll();

    if (isDragging.current) {
      if (lassoRef.current) {
        lassoRef.current.style.display = 'none';
      }
      isDragging.current = false;
      setIsDragSelecting(false);
      startPage.current = null;
      currentClientPos.current = null;

      // Block the next click so tile onClick doesn't fire
      const container = containerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          const blocker = (e) => { e.stopPropagation(); e.preventDefault(); };
          container.addEventListener('click', blocker, { capture: true, once: true });
          setTimeout(() => container.removeEventListener('click', blocker, true), 100);
        });
      }
    }
  }, [containerRef, stopAutoScroll]);

  // Stable refs so window event listeners always call the latest version
  const stableMove = useRef(null);
  const stableUp = useRef(null);
  const handleMouseMoveRef = useRef(handleMouseMove);
  const handleMouseUpRef = useRef(handleMouseUp);
  handleMouseMoveRef.current = handleMouseMove;
  handleMouseUpRef.current = handleMouseUp;
  if (!stableMove.current) {
    stableMove.current = (e) => handleMouseMoveRef.current(e);
    stableUp.current = (e) => handleMouseUpRef.current(e);
  }

  /** Call from the drag-handle's onMouseDown */
  const startDrag = useCallback((e, imageId, currentSelected) => {
    if (!enabled) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    isDragging.current = true;
    setIsDragSelecting(true);

    // Store start as page coords so rectangle tracks document during scroll
    startPage.current = { px: e.pageX, py: e.pageY };
    currentClientPos.current = { x: e.clientX, y: e.clientY };

    // Seed base selection with current + anchor tile
    baseSelection.current = [...new Set([...currentSelected, imageId])];
    onSelectionChangeRef.current([...baseSelection.current]);

    window.addEventListener('mousemove', stableMove.current);
    window.addEventListener('mouseup', stableUp.current);
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => {
    const move = stableMove.current;
    const up = stableUp.current;
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  return { lassoRef, isDragSelecting, startDrag };
}
